// src/components/agents/AgentDetailModal.tsx
//
// Modal de detalhe do agente (Reforma UX §6.6, AGT-03/05/06 — wireframe
// dlgAgent, panels am-det/am-soul/am-user/am-mem/am-cfg/am-tls/am-exe/am-hb).
// O detalhe deixa de ser página: abre sobre a lista, com abas:
//   Detalhes · SOUL · USER · Memory · Config · Tools · Execuções · Heartbeat
// (SOUL/USER/Memory/Heartbeat só pra companion).
//
// Dados: GET /v1/agents/:slug (identity_md/soul_md/user_md/memory/config),
// GET /v1/agents/:slug/executions (+ realtime via agent_executions).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type AgentDetail,
  type AgentExecution,
} from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Tabs, type TabItem } from '@/components/ui/Tabs';
import { Textarea } from '@/components/ui/Textarea';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { useSupabaseRealtime } from '@/hooks/useSupabaseRealtime';
import { cn } from '@/lib/utils';
import {
  Activity,
  BookOpen,
  Bot,
  ChevronDown,
  ChevronRight,
  FileText,
  HeartPulse,
  Save,
  Settings,
  Sparkles,
  User,
  Wrench,
} from 'lucide-react';
import { AgentToolsPanel } from './AgentToolsPanel';
import { AgentModelSelect } from './AgentModelSelect';
import { STATUS_BADGE, parseModelPolicy } from './agent-constants';
import { fmtDateTime, fmtNumber } from '@/lib/format';
// AGT-06: feed de execuções no padrão do wireframe (.afeed/.arow) + clique
// abre o MESMO modal de atividade da tela de Atividade (dlgActivity).
import { ActivityDetailModal } from '@/components/activity/ActivityDetailModal';
import { useMCStore } from '@/stores/mc-store';
import { relativeTime, type ActivityItem, type ActivityStatus } from '@/lib/activity';

type ModalTab =
  | 'details'
  | 'identity'
  | 'soul'
  | 'user'
  | 'memory'
  | 'config'
  | 'tools'
  | 'executions'
  | 'heartbeat';

interface AgentDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Slug ou id do agente (mesmo aceito por GET /v1/agents/:slug). */
  agentRef: string;
  onError?: (message: string) => void;
}

export function AgentDetailModal({ open, onOpenChange, agentRef, onError }: AgentDetailModalProps) {
  const [agent, setAgent] = useState<AgentDetail | null>(null);
  const [executions, setExecutions] = useState<AgentExecution[]>([]);
  const [executionsLoading, setExecutionsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<ModalTab>('details');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // SOUL / USER drafts (PATCH soul_md/user_md — suportado pelo backend)
  const [soulDraft, setSoulDraft] = useState('');
  const [userDraft, setUserDraft] = useState('');
  const [savingSoul, setSavingSoul] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [soulMsg, setSoulMsg] = useState('');
  const [userMsg, setUserMsg] = useState('');
  // AGT-08: aba Identity (não-companion) — editor do IDENTITY.md, como a aba
  // SOUL dos companions (wireframe v18).
  const [identityDraft, setIdentityDraft] = useState('');
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [identityMsg, setIdentityMsg] = useState('');

  // Memory: filtro por tipo + busca + expand
  const [expandedTopic, setExpandedTopic] = useState<string | null>(null);
  const [memoryTypeFilter, setMemoryTypeFilter] = useState('all');
  const [memorySearch, setMemorySearch] = useState('');

  useEffect(() => {
    if (!open || !agentRef) return;
    let alive = true;
    setLoading(true);
    setError('');
    setActiveTab('details');
    // AGT-06: a modal abre assim que o DETALHE chega — as execuções carregam
    // em paralelo SEM gatear o `loading` (antes o Promise.all segurava a
    // abertura da modal inteira esperando o endpoint de execuções).
    // AGT-08: skipMemories — memórias (maior peso do payload do companion)
    // carregam lazy quando a aba Memory abre.
    api
      .getAgent(agentRef, { skipMemories: true })
      .then((ag) => {
        if (!alive) return;
        setAgent(ag);
        setSoulDraft(ag.soul_md ?? '');
        setUserDraft(ag.user_md ?? '');
        setIdentityDraft(ag.identity_md ?? '');
      })
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Erro ao carregar agente');
      })
      .finally(() => alive && setLoading(false));
    setExecutionsLoading(true);
    api
      .listAgentExecutions(agentRef)
      .catch(() => [] as AgentExecution[])
      .then((execs) => {
        if (!alive) return;
        setExecutions(execs);
      })
      .finally(() => alive && setExecutionsLoading(false));
    return () => {
      alive = false;
    };
  }, [open, agentRef]);

  // AGT-06 — execuções em realtime, filtradas pelo agente (tabela
  // agent_executions; mesmo padrão do MCDemandsPage com useSupabaseRealtime).
  const refetchExecutions = useCallback(() => {
    if (!agentRef) return;
    api
      .listAgentExecutions(agentRef)
      .then(setExecutions)
      .catch(() => {});
  }, [agentRef]);

  const realtimeStatus = useSupabaseRealtime(
    {
      table: 'agent_executions',
      filter: agent?.id ? `agent_id=eq.${agent.id}` : undefined,
    },
    refetchExecutions,
  );

  // AGT-12: polling de fallback (20s) enquanto a aba Execuções está aberta —
  // o realtime do Supabase respeita RLS, então eventos de OUTROS tenants
  // (agentes de pipeline rodando no tenant engenharia) nunca chegam no push.
  useEffect(() => {
    if (!open || activeTab !== 'executions') return;
    const t = setInterval(refetchExecutions, 20_000);
    return () => clearInterval(t);
  }, [open, activeTab, refetchExecutions]);

  const isCompanion = agent?.archetype === 'companion';

  // AGT-08: memórias LAZY — só busca quando a aba Memory abre (cache 60s).
  // AGT-12: endpoint DEDICADO /memories (antes re-buscava o detalhe inteiro —
  // storage + count + tools — só pra ler as memórias; era a lentidão da aba).
  const { data: memoriesData, isLoading: memoriesLoading } = useQuery({
    queryKey: ['agent-memories', agentRef],
    queryFn: () => api.getAgentMemories(agentRef),
    enabled: open && isCompanion && activeTab === 'memory',
    staleTime: 60_000,
  });

  // AGT-09: prefetch das tools assim que o detalhe chega — a aba Tools abre
  // já com os dados em cache (antes os 2 GETs admin só disparavam ao clicar
  // na aba; mesmas queryKeys/staleTime do AgentToolsPanel).
  const queryClient = useQueryClient();
  const agentUuid = agent?.id;
  useEffect(() => {
    if (!open || !agentUuid) return;
    void queryClient.prefetchQuery({
      queryKey: ['admin-agent-tools', agentUuid],
      queryFn: () => api.listAdminAgentTools({ agent_id: agentUuid }).catch(() => null),
      staleTime: 60_000,
    });
    void queryClient.prefetchQuery({
      queryKey: ['admin-tools-catalog'],
      queryFn: () => api.listAdminTools().catch(() => null),
      staleTime: 5 * 60_000,
    });
  }, [open, agentUuid, queryClient]);

  const tabItems: TabItem[] = [
    { value: 'details', label: 'Detalhes', icon: Bot },
    // AGT-08: aba Identity pra NÃO-companion (editor do IDENTITY.md, espelho
    // da aba SOUL dos companions — wireframe v18, entre Detalhes e Config).
    ...(agent && !isCompanion
      ? [{ value: 'identity', label: 'Identity', icon: FileText } as TabItem]
      : []),
    ...(isCompanion ? [{ value: 'soul', label: 'SOUL', icon: Sparkles } as TabItem] : []),
    ...(isCompanion ? [{ value: 'user', label: 'USER', icon: User } as TabItem] : []),
    ...(isCompanion ? [{ value: 'memory', label: 'Memory', icon: BookOpen } as TabItem] : []),
    { value: 'config', label: 'Config', icon: Settings },
    { value: 'tools', label: 'Tools', icon: Wrench },
    { value: 'executions', label: 'Execuções', icon: Activity },
    ...(isCompanion
      ? [{ value: 'heartbeat', label: 'Heartbeat', icon: HeartPulse } as TabItem]
      : []),
  ];

  const handleSaveIdentity = async () => {
    if (!agent) return;
    setSavingIdentity(true);
    setIdentityMsg('');
    try {
      await api.updateAgent(agent.entity_id ?? agent.id, { identity_md: identityDraft });
      setIdentityMsg('IDENTITY salvo com sucesso');
    } catch (e) {
      setIdentityMsg(e instanceof Error ? e.message : 'Erro ao salvar IDENTITY');
    } finally {
      setSavingIdentity(false);
    }
  };

  const handleSaveSoul = async () => {
    if (!agent) return;
    setSavingSoul(true);
    setSoulMsg('');
    try {
      await api.updateAgent(agent.entity_id ?? agent.id, { soul_md: soulDraft });
      setSoulMsg('SOUL salvo com sucesso');
    } catch (e) {
      setSoulMsg(e instanceof Error ? e.message : 'Erro ao salvar SOUL');
    } finally {
      setSavingSoul(false);
    }
  };

  const handleSaveUser = async () => {
    if (!agent) return;
    setSavingUser(true);
    setUserMsg('');
    try {
      await api.updateAgent(agent.entity_id ?? agent.id, { user_md: userDraft });
      setUserMsg('USER salvo com sucesso');
    } catch (e) {
      setUserMsg(e instanceof Error ? e.message : 'Erro ao salvar USER');
    } finally {
      setSavingUser(false);
    }
  };

  const lastExecution = executions.length > 0 ? executions[0].started_at : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" aria-describedby={undefined} className="max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Bot className="w-5 h-5 text-raios-accent-text" />
            {agent?.name ?? 'Agente'}
            {agent && (
              <>
                <span className="font-mono text-xs font-normal text-raios-text-secondary">
                  {agent.slug}
                </span>
                <Badge variant={STATUS_BADGE[agent.status] ?? 'default'} className="text-[10px]">
                  {agent.status}
                </Badge>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4 mt-4">
            <Skeleton className="h-8 rounded-lg" />
            <Skeleton className="h-48 rounded-xl" />
          </div>
        ) : error || !agent ? (
          <div className="mt-4 rounded-lg bg-raios-danger/10 p-4">
            <p className="text-sm text-raios-danger-text">{error || 'Agente não encontrado'}</p>
          </div>
        ) : (
          <>
            <Tabs
              items={tabItems}
              value={activeTab}
              onChange={(v) => setActiveTab(v as ModalTab)}
              className="mt-4 mb-4"
              size="sm"
            />

            {/* ─── Detalhes ─────────────────────────────────────────────── */}
            {activeTab === 'details' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <MetaField label="Archetype" value={agent.archetype} />
                  <MetaField label="Slug" value={agent.slug} mono />
                  <MetaField label="Scope" value={agent.scope} />
                  <MetaField label="Status" value={agent.status} />
                  <MetaField
                    label="Criado em"
                    value={new Date(agent.created_at).toLocaleDateString('pt-BR')}
                  />
                  <MetaField
                    label="Última execução"
                    value={lastExecution ? new Date(lastExecution).toLocaleString('pt-BR') : '—'}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-raios-text-secondary">
                      IDENTITY.md
                    </h3>
                    {agent.identity_md_path && (
                      <span className="text-[10px] font-mono text-raios-muted">
                        {agent.identity_md_path}
                      </span>
                    )}
                  </div>
                  {agent.identity_md ? (
                    <pre className="whitespace-pre-wrap break-words text-xs font-mono text-raios-text-secondary bg-raios-surface rounded-md p-4 max-h-[380px] overflow-auto">
                      {agent.identity_md}
                    </pre>
                  ) : (
                    <p className="text-xs text-raios-muted italic">Identity.md não disponível.</p>
                  )}
                </div>
              </div>
            )}

            {/* ─── Identity (não-companion) — AGT-08, wireframe v18 ─────── */}
            {activeTab === 'identity' && !isCompanion && (
              <MarkdownEditor
                heading="IDENTITY.md · papel, domínio e regras do agente"
                value={identityDraft}
                onChange={setIdentityDraft}
                onSave={handleSaveIdentity}
                saving={savingIdentity}
                message={identityMsg}
                saveLabel="Salvar IDENTITY"
              />
            )}

            {/* ─── SOUL (companion) ─────────────────────────────────────── */}
            {activeTab === 'soul' && isCompanion && (
              <MarkdownEditor
                heading="SOUL.md · voz, valores e limites (companion)"
                value={soulDraft}
                onChange={setSoulDraft}
                onSave={handleSaveSoul}
                saving={savingSoul}
                message={soulMsg}
                saveLabel="Salvar SOUL"
              />
            )}

            {/* ─── USER (companion) ─────────────────────────────────────── */}
            {activeTab === 'user' && isCompanion && (
              <MarkdownEditor
                heading="USER.md · contexto do dono (companion)"
                value={userDraft}
                onChange={setUserDraft}
                onSave={handleSaveUser}
                saving={savingUser}
                message={userMsg}
                saveLabel="Salvar USER"
              />
            )}

            {/* ─── Memory (companion) — AGT-08: carrega lazy com cache ───── */}
            {activeTab === 'memory' && isCompanion && (
              <MemoryPanel
                topics={memoriesData?.topic_files ?? []}
                loading={memoriesLoading}
                expandedTopic={expandedTopic}
                setExpandedTopic={setExpandedTopic}
                memoryTypeFilter={memoryTypeFilter}
                setMemoryTypeFilter={setMemoryTypeFilter}
                memorySearch={memorySearch}
                setMemorySearch={setMemorySearch}
              />
            )}

            {/* ─── Config ───────────────────────────────────────────────── */}
            {activeTab === 'config' && (
              <ConfigPanel agent={agent} onError={onError} />
            )}

            {/* ─── Tools ────────────────────────────────────────────────── */}
            {activeTab === 'tools' && (
              <AgentToolsPanel
                agentId={agent.id}
                initialAllowed={agent.config?.allowed_tools ?? []}
                onError={onError}
              />
            )}

            {/* ─── Execuções (realtime) ─────────────────────────────────── */}
            {activeTab === 'executions' && (
              <ExecutionsPanel
                executions={executions}
                realtimeStatus={realtimeStatus}
                loading={executionsLoading}
              />
            )}

            {/* ─── Heartbeat (companion) ────────────────────────────────── */}
            {activeTab === 'heartbeat' && isCompanion && (
              <HeartbeatPanel agent={agent} onError={onError} />
            )}
          </>
        )}
      </DialogContent>
      {/* AGT-06: modal de atividade (wireframe dlgActivity) — reuso do mesmo
          componente da tela de Atividade; abre ao clicar numa execução. */}
      <ActivityDetailModal />
    </Dialog>
  );
}

/* ─── Sub-painéis ───────────────────────────────────────────────────────────── */

function MetaField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md border border-raios-border-muted bg-raios-surface px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-raios-muted">{label}</p>
      <p className={cn('text-sm text-raios-text mt-0.5', mono && 'font-mono text-xs break-all')}>
        {value}
      </p>
    </div>
  );
}

function MarkdownEditor({
  heading,
  value,
  onChange,
  onSave,
  saving,
  message,
  saveLabel,
}: {
  heading: string;
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  saving: boolean;
  message: string;
  saveLabel: string;
}) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-raios-text-secondary">
        {heading}
      </h3>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={14}
        className="min-h-[300px] font-mono"
      />
      <div className="flex items-center justify-between">
        {message && (
          <p
            className={cn(
              'text-xs',
              message.includes('sucesso') ? 'text-raios-success-text' : 'text-raios-danger-text',
            )}
          >
            {message}
          </p>
        )}
        <Button variant="brand" size="sm" onClick={onSave} loading={saving} className="ml-auto">
          <Save className="w-4 h-4" />
          {saveLabel}
        </Button>
      </div>
    </div>
  );
}

const MEMORY_TYPE_LABELS: Record<string, string> = {
  session_note: 'Sessão',
  topic_file: 'Tópico',
  decision: 'Decisão',
  daily_note: 'Diário',
  must_not_lose: 'Crítico',
  reference_doc: 'Referência',
  index_seed: 'Seed',
  outros: 'Outros',
};

function MemoryPanel({
  topics,
  loading,
  expandedTopic,
  setExpandedTopic,
  memoryTypeFilter,
  setMemoryTypeFilter,
  memorySearch,
  setMemorySearch,
}: {
  // AGT-08: memórias chegam LAZY (query da aba) — não mais via agent.memory.
  topics: NonNullable<NonNullable<AgentDetail['memory']>['topic_files']>;
  loading: boolean;
  expandedTopic: string | null;
  setExpandedTopic: (v: string | null) => void;
  memoryTypeFilter: string;
  setMemoryTypeFilter: (v: string) => void;
  memorySearch: string;
  setMemorySearch: (v: string) => void;
}) {
  if (loading && topics.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  const typeCounts = topics.reduce<Record<string, number>>((acc, t) => {
    const k = t.type ?? 'outros';
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});
  const filtered = topics.filter((t) => {
    if (memoryTypeFilter !== 'all' && (t.type ?? 'outros') !== memoryTypeFilter) return false;
    if (memorySearch.trim()) {
      const q = memorySearch.toLowerCase();
      const hay = `${t.title ?? ''} ${t.preview ?? ''} ${t.content ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-raios-text-secondary">
          Memórias ({filtered.length}
          {filtered.length !== topics.length ? ` de ${topics.length}` : ''})
        </h3>
        <input
          type="search"
          value={memorySearch}
          onChange={(e) => setMemorySearch(e.target.value)}
          placeholder="Buscar memória…"
          className="text-xs px-3 py-1.5 rounded border border-raios-border bg-transparent text-raios-text placeholder:text-raios-muted flex-1 max-w-xs"
        />
      </div>
      {topics.length > 0 && (
        <div className="flex items-center gap-1.5 mb-4 flex-wrap">
          <MemoryTypeChip
            active={memoryTypeFilter === 'all'}
            label={`Todas (${topics.length})`}
            onClick={() => setMemoryTypeFilter('all')}
          />
          {Object.entries(typeCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([type, count]) => (
              <MemoryTypeChip
                key={type}
                active={memoryTypeFilter === type}
                label={`${MEMORY_TYPE_LABELS[type] ?? type} (${count})`}
                onClick={() => setMemoryTypeFilter(type)}
              />
            ))}
        </div>
      )}
      {filtered.length > 0 ? (
        <ul className="space-y-2 max-h-[55vh] overflow-y-auto">
          {filtered.map((t) => {
            const isExpanded = expandedTopic === t.path;
            const hasContent = Boolean(t.content && t.content.trim());
            return (
              <li key={t.path} className="rounded border border-raios-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedTopic(isExpanded ? null : t.path)}
                  className="w-full flex items-start gap-2 p-3 text-left hover:bg-raios-surface transition-colors"
                >
                  {hasContent ? (
                    isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-raios-muted mt-0.5 shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-raios-muted mt-0.5 shrink-0" />
                    )
                  ) : (
                    <BookOpen className="w-4 h-4 text-raios-muted mt-0.5 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-raios-text">
                        {t.title ?? t.path}
                      </span>
                      {t.type && (
                        <Badge variant="default" className="text-[10px]">
                          {MEMORY_TYPE_LABELS[t.type] ?? t.type}
                        </Badge>
                      )}
                      {t.importance != null && t.importance >= 4 && (
                        <span className="text-[10px] text-raios-warning-text">
                          ★ {t.importance}
                        </span>
                      )}
                      {t.updated_at && (
                        <span className="text-[10px] text-raios-muted ml-auto">
                          {new Date(t.updated_at).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                    {!isExpanded && t.preview && (
                      <p className="text-xs text-raios-muted mt-1 line-clamp-2">{t.preview}</p>
                    )}
                  </div>
                </button>
                {isExpanded && hasContent && (
                  <pre className="whitespace-pre-wrap break-words text-xs font-mono text-raios-text-secondary bg-raios-surface px-4 py-3 border-t border-raios-border max-h-[380px] overflow-auto">
                    {t.content}
                  </pre>
                )}
              </li>
            );
          })}
        </ul>
      ) : topics.length > 0 ? (
        <p className="text-sm text-raios-muted italic text-center py-8">
          Nenhuma memória corresponde aos filtros atuais.
        </p>
      ) : (
        <EmptyState
          icon={BookOpen}
          title="Nenhuma memória ainda"
          description="O agente preenche conforme aprende e salva memórias longas."
        />
      )}
    </div>
  );
}

function MemoryTypeChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-[11px] px-2.5 py-1 rounded-full border transition-colors',
        active
          ? 'bg-raios-brand text-white border-raios-brand'
          : 'border-raios-border text-raios-text-secondary hover:bg-raios-surface',
      )}
    >
      {label}
    </button>
  );
}

function ConfigPanel({
  agent,
  onError,
}: {
  agent: AgentDetail;
  onError?: (message: string) => void;
}) {
  const initialPolicy = parseModelPolicy(agent.config?.model_policy);
  // Mantém a policy mais recente do seletor. Sem isso, salvar temperatura após
  // editar a cadeia de fallback reenviaria o snapshot antigo e perderia a ordem.
  const [currentPolicy, setCurrentPolicy] = useState(initialPolicy);
  const [temperature, setTemperature] = useState(initialPolicy.temperature ?? 0.8);
  const [tempMsg, setTempMsg] = useState('');
  const [savingTemp, setSavingTemp] = useState(false);

  const saveTemperature = async () => {
    setSavingTemp(true);
    setTempMsg('');
    try {
      const nextPolicy = { ...currentPolicy, temperature };
      // Contrato pós-fix: PATCH aceita model_policy top-level. Backend antigo
      // responde 422 → degrade graceful (toast), seleção local mantida.
      await api.updateAgent(agent.entity_id ?? agent.id, {
        model_policy: nextPolicy,
      });
      setCurrentPolicy(nextPolicy);
      setTempMsg('Temperatura salva com sucesso');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao salvar temperatura';
      setTempMsg(`Não persistido (backend antigo não aceita model_policy): ${msg}`);
      onError?.(msg);
    } finally {
      setSavingTemp(false);
    }
  };

  // Wireframe am-cfg: provedor + modelo + temperatura. Bloco "skills
  // permitidas" REMOVIDO — todas as skills estão sempre disponíveis.
  return (
    <div className="space-y-5 max-w-2xl">
      <AgentModelSelect
        agentRef={agent.entity_id ?? agent.id}
        initial={currentPolicy}
        onError={onError}
        onSaved={setCurrentPolicy}
      />
      <div>
        <label
          htmlFor="agent-temperature"
          className="block text-[10px] font-semibold uppercase tracking-wide text-raios-muted mb-2"
        >
          Temperatura
        </label>
        <div className="flex items-center gap-3">
          <input
            id="agent-temperature"
            type="range"
            min={0}
            max={1}
            step={0.1}
            value={temperature}
            onChange={(e) => setTemperature(Number(e.target.value))}
            className="flex-1 accent-raios-accent"
          />
          <span className="text-sm font-mono text-raios-text w-8 text-right">
            {fmtNumber(temperature, 1)}
          </span>
          <Button variant="outline" size="sm" loading={savingTemp} onClick={saveTemperature}>
            <Save className="w-3.5 h-3.5" />
            Salvar
          </Button>
        </div>
        {tempMsg && (
          <p
            className={cn(
              'text-xs mt-2',
              tempMsg.includes('sucesso') ? 'text-raios-success-text' : 'text-raios-danger-text',
            )}
          >
            {tempMsg}
          </p>
        )}
      </div>
    </div>
  );
}

// AGT-06 — status do agent_executions → ActivityStatus (vocabulário do
// ActivityDetailModal/wireframe).
function execToActivityStatus(status: string): ActivityStatus {
  if (status === 'completed' || status === 'success') return 'completed';
  if (status === 'failed' || status === 'error') return 'failed';
  if (status === 'running') return 'running';
  if (status === 'cancelled') return 'cancelled';
  if (status === 'timeout') return 'timeout';
  return 'partial';
}

// Cor do dot por status — réplica do wireframe (.adot / .adot.ok/.warn/.bad).
function execDotClass(status: string): string {
  const s = execToActivityStatus(status);
  if (s === 'completed') return 'bg-raios-success';
  if (s === 'failed') return 'bg-raios-danger';
  if (s === 'running') return 'bg-raios-accent animate-pulse';
  return 'bg-raios-warning';
}

// ActivityItem mínimo a partir de uma execução do agente — o detalhe completo
// (input/output/erro) é carregado pelo openActivityDetail via
// GET /v1/activity/agent/:id.
function execToActivityItem(e: AgentExecution): ActivityItem {
  return {
    kind: 'agent',
    id: e.id,
    actor_id: e.agent_id ?? null,
    actor_slug: null,
    actor_name: null,
    actor_archetype: null,
    actor_role: null,
    actor_scope: null,
    parent_actor_id: null,
    parent_actor_slug: null,
    parent_actor_name: null,
    invoker_type: null,
    status: execToActivityStatus(e.status),
    started_at: e.started_at,
    ended_at: e.completed_at ?? e.ended_at ?? null,
    duration_ms: e.duration_ms ?? null,
    cost_usd: e.cost_usd ?? null,
    tokens_input: e.tokens_input ?? null,
    tokens_output: e.tokens_output ?? null,
    model_used: e.model_used ?? null,
    task_id: null,
    trace_id: e.trace_id ?? null,
    error_message: e.error ?? null,
    parent_trace_id: null,
    root_trace_id: null,
    metadata_kind: null,
    llm_calls_count: null,
    tool_calls_count: null,
    target_slug: null,
    cost_usd_partial: null,
    tokens_input_partial: null,
    tokens_output_partial: null,
    children_completed_count: null,
  };
}

function ExecutionsPanel({
  executions,
  realtimeStatus,
  loading,
}: {
  executions: AgentExecution[];
  realtimeStatus: string;
  loading: boolean;
}) {
  const openActivityDetail = useMCStore((s) => s.openActivityDetail);
  // AGT-08: paginação 10/página — sem rolagem infinita conforme execuções
  // entram em realtime (mesmo padrão da listagem de agentes).
  const EXEC_PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const sorted = useMemo(
    () => [...executions].sort((a, b) => (a.started_at < b.started_at ? 1 : -1)),
    [executions],
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / EXEC_PAGE_SIZE));
  const effectivePage = Math.min(Math.max(1, page), totalPages);
  const paginated = sorted.slice((effectivePage - 1) * EXEC_PAGE_SIZE, effectivePage * EXEC_PAGE_SIZE);
  return (
    <div className="space-y-3">
      <p className="text-xs text-raios-muted flex items-center gap-2">
        Execuções deste agente — mais recente primeiro. Clique numa execução pra
        ver métricas, input, output e erro.
        <span
          className={cn(
            'inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold',
            realtimeStatus === 'connected' ? 'text-raios-success-text' : 'text-raios-muted',
          )}
        >
          <span
            className={cn(
              'inline-block w-1.5 h-1.5 rounded-full',
              realtimeStatus === 'connected'
                ? 'bg-raios-success animate-pulse'
                : 'bg-raios-border',
            )}
          />
          real-time
        </span>
      </p>
      {loading && executions.length === 0 ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : executions.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="Sem execuções ainda"
          description="As execuções deste agente aparecem aqui em tempo real."
        />
      ) : (
        // Feed do wireframe (.afeed/.arow): UMA linha por execução — dot de
        // status + texto + tempo relativo. Sem tabela, sem scroll horizontal;
        // só vertical. Clique abre o modal de atividade.
        <>
          <div className="flex flex-col">
            {paginated.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => void openActivityDetail(execToActivityItem(e))}
                className="flex items-start gap-2.5 w-full text-left px-2 py-2 rounded-md border-b border-raios-border-muted last:border-b-0 hover:bg-raios-elevated/60 transition-colors cursor-pointer min-w-0"
              >
                <span
                  className={cn('mt-1.5 w-[7px] h-[7px] rounded-full shrink-0', execDotClass(e.status))}
                  aria-hidden
                />
                <span className="flex-1 min-w-0 text-[0.8rem] text-raios-text-secondary truncate">
                  {e.trigger?.trim() ? (
                    // AGT-09: o backend corta o trigger em 120 chars — quando
                    // bate o corte, sinaliza com reticências explícitas (sem
                    // elas o texto cortado parece bug pro usuário).
                    `${e.trigger.trim()}${e.trigger.length >= 120 ? '…' : ''}`
                  ) : (
                    <span className="italic text-raios-muted">
                      execução {execToActivityStatus(e.status) === 'running' ? 'em andamento' : 'sem prompt registrado'}
                    </span>
                  )}
                </span>
                {e.duration_ms != null && (
                  <span className="font-mono text-[0.62rem] text-raios-muted whitespace-nowrap mt-0.5">
                    {fmtNumber(e.duration_ms / 1000, 1)}s
                  </span>
                )}
                {/* AGT-09: hora da execução visível (além do relativo). */}
                <span
                  className="font-mono text-[0.62rem] text-raios-muted whitespace-nowrap mt-0.5"
                  title={fmtDateTime(e.started_at)}
                >
                  {relativeTime(e.started_at)} ·{' '}
                  {new Date(e.started_at).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </button>
            ))}
          </div>
          {sorted.length > EXEC_PAGE_SIZE && (
            <div className="flex items-center justify-between px-1 pt-2">
              <p className="text-xs text-raios-muted">
                Página {effectivePage} de {totalPages} · {sorted.length}{' '}
                {sorted.length === 1 ? 'execução' : 'execuções'}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={effectivePage === 1}
                  onClick={() => setPage(Math.max(1, effectivePage - 1))}
                  aria-label="Página anterior de execuções"
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={effectivePage >= totalPages}
                  onClick={() => setPage(Math.min(totalPages, effectivePage + 1))}
                  aria-label="Próxima página de execuções"
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const HEARTBEAT_UNITS = [
  { value: 'seconds', label: 'segundos' },
  { value: 'milliseconds', label: 'milissegundos' },
  { value: 'minutes', label: 'minutos' },
  { value: 'hours', label: 'horas' },
];

function HeartbeatPanel({
  agent,
  onError,
}: {
  agent: AgentDetail;
  onError?: (message: string) => void;
}) {
  // AGT-12: heartbeat REAL — GET /v1/agents/:slug/heartbeat traz config
  // (default LIGADO, 30min), último beat e registros (beats + notificações
  // proativas). Switch liga/desliga via PATCH heartbeat.enabled.
  const agentRef = agent.entity_id ?? agent.id;
  const queryClient = useQueryClient();
  const { data: hb, isLoading: hbLoading } = useQuery({
    queryKey: ['agent-heartbeat', agentRef],
    queryFn: () => api.getAgentHeartbeat(agentRef),
    staleTime: 30_000,
  });

  const [intervalValue, setIntervalValue] = useState('30');
  const [unit, setUnit] = useState('minutes');
  const [enabled, setEnabled] = useState(true);
  // AGT-13: sub-abas dos registros — Follow-ups (wireframe v18) + Histórico.
  const [registrosTab, setRegistrosTab] = useState<'followups' | 'history'>('followups');
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!hb || hydrated) return;
    setEnabled(hb.config.enabled);
    setIntervalValue(String(hb.config.interval_value));
    setUnit(hb.config.interval_unit);
    setHydrated(true);
  }, [hb, hydrated]);

  const persist = async (nextEnabled: boolean, nextValue: string, nextUnit: string) => {
    setSaving(true);
    setMsg('');
    try {
      await api.updateAgent(agentRef, {
        heartbeat: {
          enabled: nextEnabled,
          interval_value: Number(nextValue) || 30,
          interval_unit: nextUnit,
        },
      });
      setMsg('Heartbeat salvo com sucesso');
      void queryClient.invalidateQueries({ queryKey: ['agent-heartbeat', agentRef] });
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Erro ao salvar heartbeat';
      setMsg(`Não persistido: ${m}`);
      onError?.(m);
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = (next: boolean) => {
    setEnabled(next);
    void persist(next, intervalValue, unit);
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <p className="text-xs text-raios-muted">
        Heartbeat — mecanismo que acorda o companion no intervalo pra verificar o que acompanha e
        avisar o usuário proativamente quando há algo novo.
      </p>
      <div className="flex items-center gap-3">
        {/* Switch ligar/desligar (AGT-12 — era read-only) */}
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={`${enabled ? 'Desligar' : 'Ligar'} heartbeat`}
          disabled={saving || hbLoading}
          onClick={() => toggleEnabled(!enabled)}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors',
            enabled ? 'bg-raios-success/20 border-raios-success' : 'bg-raios-surface border-raios-border',
            (saving || hbLoading) && 'opacity-60 cursor-not-allowed',
          )}
        >
          <span
            className={cn(
              'inline-block h-3.5 w-3.5 rounded-full transition-transform',
              enabled ? 'translate-x-[18px] bg-raios-success' : 'translate-x-[3px] bg-raios-muted',
            )}
          />
        </button>
        <span className={cn('text-sm', enabled ? 'text-raios-success-text' : 'text-raios-muted')}>
          {enabled ? 'Ligado' : 'Desligado'}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <MetaField
          label="Último beat"
          value={
            hbLoading ? '…' : hb?.last_beat_at ? `${relativeTime(hb.last_beat_at)} · ${fmtDateTime(hb.last_beat_at)}` : '—'
          }
        />
        <MetaField
          label="Beats (últimos 10)"
          value={hbLoading ? '…' : String(hb?.beats.length ?? 0)}
        />
        <MetaField
          label="Notificações proativas"
          value={hbLoading ? '…' : String(hb?.notifications.length ?? 0)}
        />
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-raios-muted mb-2">
          Intervalo do heartbeat
        </p>
        <div className="flex items-end gap-2">
          <Input
            type="number"
            min={1}
            value={intervalValue}
            onChange={(e) => setIntervalValue(e.target.value)}
            className="max-w-[120px]"
            aria-label="Valor do intervalo do heartbeat"
          />
          <Select
            options={HEARTBEAT_UNITS}
            value={unit}
            onChange={setUnit}
            className="min-w-[150px]"
          />
          <Button
            variant="outline"
            size="sm"
            loading={saving}
            onClick={() => void persist(enabled, intervalValue, unit)}
          >
            <Save className="w-3.5 h-3.5" />
            Salvar
          </Button>
        </div>
        <p className="text-xs text-raios-muted mt-1.5">
          Padrão: 30 minutos. A varredura do sistema respeita o intervalo de cada companion.
        </p>
        {msg && (
          <p
            className={cn(
              'text-xs mt-2',
              msg.includes('sucesso') ? 'text-raios-success-text' : 'text-raios-danger-text',
            )}
          >
            {msg}
          </p>
        )}
      </div>
      <div>
        {/* AGT-13 — duas sub-abas (pedido do admin): Follow-ups do Companion
            (wireframe v18 hblog: checagens AGENDADAS pro beat executar) +
            Histórico (beats e avisos proativos já ocorridos). */}
        <div className="flex items-center gap-1.5 mb-3" role="tablist" aria-label="Registros do heartbeat">
          <button
            type="button"
            role="tab"
            aria-selected={registrosTab === 'followups'}
            onClick={() => setRegistrosTab('followups')}
            className={cn(
              'text-xs px-3 py-1.5 rounded-full border transition-colors',
              registrosTab === 'followups'
                ? 'border-raios-accent text-raios-accent-text bg-raios-accent/10'
                : 'border-raios-border text-raios-muted hover:text-raios-text',
            )}
          >
            Follow-ups do Companion{hb?.followups?.length ? ` (${hb.followups.length})` : ''}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={registrosTab === 'history'}
            onClick={() => setRegistrosTab('history')}
            className={cn(
              'text-xs px-3 py-1.5 rounded-full border transition-colors',
              registrosTab === 'history'
                ? 'border-raios-accent text-raios-accent-text bg-raios-accent/10'
                : 'border-raios-border text-raios-muted hover:text-raios-text',
            )}
          >
            Histórico
          </button>
        </div>
        {registrosTab === 'followups' ? (
          hbLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : (hb?.followups?.length ?? 0) === 0 ? (
            <EmptyState
              icon={HeartPulse}
              title="Sem follow-ups agendados"
              description="Quando o companion registrar checagens pra fazer (devolver resultado de pipeline, verificar demanda, lembrete), elas aparecem aqui com a data prevista."
            />
          ) : (
            <div className="flex flex-col">
              {(hb?.followups ?? []).map((f, i) => (
                <div
                  key={`f-${i}`}
                  className="flex items-start gap-2.5 px-2 py-2 border-b border-raios-border-muted last:border-b-0"
                >
                  <span
                    className="font-mono text-[0.62rem] text-raios-muted whitespace-nowrap mt-0.5 shrink-0"
                    title={fmtDateTime(f.due_at)}
                  >
                    {new Date(f.due_at).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <span className="flex-1 min-w-0 text-[0.8rem] text-raios-text-secondary">
                    {f.instruction || <span className="italic text-raios-muted">checagem agendada</span>}
                  </span>
                </div>
              ))}
            </div>
          )
        ) : hbLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : (hb?.beats.length ?? 0) === 0 && (hb?.notifications.length ?? 0) === 0 ? (
          <EmptyState
            icon={HeartPulse}
            title="Sem beats ainda"
            description="Os ticks do heartbeat e os avisos proativos enviados ao usuário aparecem aqui."
          />
        ) : (
          <div className="flex flex-col">
            {(hb?.notifications ?? []).map((n, i) => (
              <div
                key={`n-${i}`}
                className="flex items-start gap-2.5 px-2 py-2 border-b border-raios-border-muted last:border-b-0"
              >
                <span className="mt-1.5 w-[7px] h-[7px] rounded-full shrink-0 bg-raios-accent" aria-hidden />
                <span className="flex-1 min-w-0 text-[0.8rem] text-raios-text-secondary">
                  {n.message || <span className="italic text-raios-muted">aviso proativo</span>}
                </span>
                <span className="font-mono text-[0.62rem] text-raios-muted whitespace-nowrap mt-0.5" title={fmtDateTime(n.at)}>
                  {n.delivered_at ? 'entregue' : 'pendente'} · {relativeTime(n.at)}
                </span>
              </div>
            ))}
            {(hb?.beats ?? []).map((b, i) => (
              <div
                key={`b-${i}`}
                className="flex items-start gap-2.5 px-2 py-2 border-b border-raios-border-muted last:border-b-0"
              >
                <span
                  className={cn(
                    'mt-1.5 w-[7px] h-[7px] rounded-full shrink-0',
                    b.notified ? 'bg-raios-success' : b.status === 'failed' ? 'bg-raios-danger' : 'bg-raios-border',
                  )}
                  aria-hidden
                />
                <span className="flex-1 min-w-0 text-[0.8rem] text-raios-text-secondary">
                  beat {b.notified ? 'com aviso ao usuário' : b.silent ? 'silencioso (nada novo)' : b.status}
                  {b.reason ? <span className="text-raios-muted"> · {b.reason}</span> : null}
                </span>
                <span className="font-mono text-[0.62rem] text-raios-muted whitespace-nowrap mt-0.5" title={fmtDateTime(b.at)}>
                  {relativeTime(b.at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
